import output from "_src/engine/output";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import { transformVecMat4 } from "_src/engine/math";

import { vec3, vec4, mat4 } from "_src/libs/gl-matrix";

const _VEC3_TMP = vec3.create();
const _VEC3_TMP2 = vec3.create();
const _VEC3_TMP3 = vec3.create();

const _MAT4_TMP = mat4.create();

const EPSILON = 0.001;

export default {

  checkSelectionMask(selectionMask) {

    for (let i = 0; i < selectionMask.length; ++i) {
      if (selectionMask[i]) {
        return true;
      }
    }

    return false;

  },

  getMaxBoundings(selectionMask, width, height) {

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < height; ++i) {
      for (let j = 0; j < width; ++j) {
        const offset = i * width + j;
        if (selectionMask[offset]) {
          minX = Math.min(minX, j);
          maxX = Math.max(maxX, j);
          minY = Math.min(minY, i);
          maxY = Math.max(maxY, i);
        }
      }
    }

    return [minX, minY, maxX, maxY];

  },

  selectByMask: (function() {

    const mat4Tmp = mat4.create();

    return function (object, camera, selectionBuffer, depthBuffer, bounding, oneSideSelection) {

      const width = camera.viewport[2];
      const height = camera.viewport[3];

      const frames = object.frames;

      let totalSelected = 0;

      for (let frame of frames) {

        // NOTE: decimation requires this check
        if (!frame.loaded) {
          continue;
        }

        const model = frame.model;
        const chunks = model.chunks;

        if (!chunks.length) {
          continue;
        }

        const mvpMatrix = mat4.multiply(mat4Tmp, camera.viewProjMat, model.modelMatrix);

        for (let chunk of chunks) {

          const geometry = chunk.geometry;

          if (!model.isChunkInFrustum(chunk)) {
            continue;
          }

          if (oneSideSelection && !model.getChunkQueryResult(chunk)) {
            continue;
          }

          const indices = geometry.getItemBuffer("indices");
          const selectionMask = geometry.getItemBuffer("active_selection_mask");
          const positions = geometry.getItemBuffer("a_position");

          if (!positions || !selectionMask) {
            output.error(output.DEVELOPERS_ERROR, "Invalid geometry. Missing positions or selection mask. Aborting selection.");
            return 0;
          }

          for (let z = 0; z < selectionMask.length; ++z) {
            selectionMask[z] = 0;
          }
          geometry.setHasMarkedVertices(false);

          const numSelected = geometry.octree.maskIntersect(indices, positions,
            selectionMask, selectionBuffer, depthBuffer, width, height, bounding, mvpMatrix,
            camera.near, camera.far, camera.isOrtho());

          // NOTE: it's a hack to mark chunk as selected
          if (numSelected > 0) {
            geometry.setHasMarkedVertices(true);
            totalSelected += numSelected;
          }

        }

      }

      return totalSelected;

    };

  })(),

  selectByPlane : (function() {

    const vec3Tmp = vec3.create();
    const vec4Tmp = vec4.create();
    const mat4Tmp = mat4.create();

    return function (object, normal, point, clearOldSelection = true) {

      let totalSelected = 0;

      for (const frame of object.frames) {

        // NOTE: decimation requires this check
        if (!frame.loaded) {
          continue;
        }

        const model = frame.model;
        const chunks = model.chunks;

        if (!chunks.length) {
          continue;
        }

        const toLocalMat4 = mat4.invert(mat4Tmp, model.modelMatrix);
        const localPoint = vec3.transformMat4(vec3Tmp, point, toLocalMat4);
        vec4.set(vec4Tmp, normal[0], normal[1], normal[2], 0);

        const plane = vec4.transformMat4(vec4Tmp, vec4Tmp, toLocalMat4);
        plane[3] = -vec3.dot(plane, localPoint);

        for (const chunk of chunks) {

          const geometry = chunk.geometry;
          const indices = geometry.getItemBuffer("indices");
          const selectionMask = geometry.getItemBuffer("active_selection_mask");
          const positions = geometry.getItemBuffer("a_position");

          if (!positions || !selectionMask) {
            output.error(output.DEVELOPERS_ERROR, "Invalid geometry. Missing positions or selection mask. Aborting selection.");
            return 0;
          }

          if (clearOldSelection) {
            for (let z = 0; z < selectionMask.length; ++z) {
              selectionMask[z] = 0;
            }
            geometry.setHasMarkedVertices(false);
          }

          const numSelected = geometry.octree.planeIntersect(indices, positions, selectionMask, plane);

          // NOTE: it's a hack to mark chunk as selected
          if (numSelected > 0) {
            geometry.setHasMarkedVertices(true);
            totalSelected += numSelected;
          }

        }

      }

      return totalSelected;

    };

  })(),

  selectSphere(object, center, radius) {

    let totalSelected = 0;

    //NOTE: we use only object's scale
    const scale = vec3.copy(_VEC3_TMP, object.model.scale);
    const frames = object.frames;

    for (let frame of frames) {

      const model = frame.model;
      const chunks = model.chunks;

      const toLocalMat4 = mat4.invert(_MAT4_TMP, model.modelMatrix);

      for (let chunk of chunks) {

        const geometry = chunk.geometry;

        const indices = geometry.getItemBuffer("indices");
        const selectionMask = geometry.getItemBuffer("active_selection_mask");
        const positions = geometry.getItemBuffer("a_position");

        if (!positions || !selectionMask) {
          output.error(output.DEVELOPERS_ERROR, "Invalid geometry. Missing positions or selection mask. Aborting selection.");
          return 0;
        }

        for (let k = 0; k < selectionMask.length; ++k) {
          selectionMask[k] = 0;
        }
        geometry.setHasMarkedVertices(false);

        const localPoint = vec3.transformMat4(_VEC3_TMP2, center, toLocalMat4);

        const numSelected = geometry.octree.sphereIntersect(localPoint, radius,
                                             indices, positions, selectionMask, scale);

        if (numSelected > 0) {
          // NOTE: it's a hack to mark chunk as selected
          geometry.setHasMarkedVertices(true);
          totalSelected += numSelected;
        }

      }

    }

    return totalSelected;

  },

  unselectInvisibleVertices(objects, camera) {

    const selectedChunks = [];

    const matrices = new Map();

    for (let object of objects) {
      for (let frame of object.frames) {
        for (let chunk of frame.model.chunks) {
          if (chunk.geometry.checkHasMarkedVertices()) {

            selectedChunks.push(chunk);

            matrices.set(chunk, mat4.invert(mat4.create(), frame.model.modelMatrix));

          }
        }
      }
    }

    this._unselectInvisibleVertices(selectedChunks, matrices, camera);

  },

  _unselectInvisibleVertices : (function() {

    const vec3Tmp = vec3.create();
    const vec3Tmp2 = vec3.create();
    const vec3Tmp3 = vec3.create();
    const vec3Tmp4 = vec3.create();
    const vec3Tmp5 = vec3.create();
    const vec3Tmp6 = vec3.create();
    const vec3Tmp7 = vec3.create();

    const mat4Tmp = mat4.create();

    return function(selectedChunks, matrices, camera) {

      const view = vec3.set(vec3Tmp, 0, 0, -1);
      vec3.transformQuat(view, view, camera.quat);

      for (let k = 0; k < selectedChunks.length; ++k) {

        const currentChunk = selectedChunks[k];

        const currenGeometry = currentChunk.geometry;
        const selectionMask = currenGeometry.getItemBuffer("active_selection_mask");
        const positions = currenGeometry.getItemBuffer("a_position");

        const toWorldMat4 = mat4.invert(mat4Tmp, matrices.get(currentChunk));

        const origin = vec3.copy(vec3Tmp3, camera.trans);

        for (let i = 0; i < selectionMask.length; ++i) {

          if (selectionMask[i] == 0) {
            continue;
          }

          const offset = i * 3;
          const vertex = vec3.set(vec3Tmp4, positions[offset], positions[offset + 1], positions[offset + 2]);
          const worldVertex = vec3.transformMat4(vertex, vertex, toWorldMat4);

          if (camera.isOrtho()) {
            const t = (vec3.dot(worldVertex, view) - vec3.dot(view, origin)) / vec3.dot(view, view);
            vec3.scaleAndAdd(origin, worldVertex, view, -t);
          }
          const to = vec3.sub(vec3Tmp5, worldVertex, origin);

          const vertexDistance = vec3.length(to);

          vec3.scale(to, to, 1.0 / vertexDistance);

          for (let j = 0; j < selectedChunks.length; ++j) {

            const chunk = selectedChunks[j];
            const geometry = chunk.geometry;

            const toLocalMat4 = matrices.get(chunk);

            const localOrigin = vec3.transformMat4(vec3Tmp6, origin, toLocalMat4);
            const localTo = transformVecMat4(vec3Tmp7, to, toLocalMat4);
            vec3.normalize(localTo, localTo);

            const localDistance = numberToObjectSpace(vertexDistance, toLocalMat4);

            const indices = geometry.getItemBuffer("indices");
            const positions = geometry.getItemBuffer("a_position");

            const distance = intersectEvaluator.AABBRayDistance(localOrigin, localTo, geometry.bbox);

            if (distance < 0 || localDistance < distance) {
              continue;
            }

            const intersector = (from, to, offset, length, result) => {
              return intersectEvaluator.triangleRayIntersection(from, to, indices, offset, length, positions, result);
            };

            const dist = geometry.octree.castRay(localOrigin, localTo, intersector, vec3Tmp2);

            if (dist < 0) {
              continue;
            }

            if (dist < localDistance - EPSILON) {

              selectionMask[i] = 0;
              break;

            }

          }

        }

      }

    }

  })(),

  dropSelection(object) {

    for (let frame of object.frames.filter(frame => frame.loaded)) {

      for (let chunk of frame.model.chunks) {

        const geom = chunk.geometry;

        const selectionMask = geom.getItemBuffer("a_selection_mask");

        if (selectionMask) {
          geom.dropSelection();
          chunk.markAttributeDirty("a_selection_mask");
        }

      }

    }

  },

  invertSelection(object) {

    for (let frame of object.frames.filter(frame => frame.loaded)) {

      for (let chunk of frame.model.chunks) {

        const geom = chunk.geometry;

        const selectionMask = geom.getItemBuffer("a_selection_mask");

        if (selectionMask) {
          geom.invertSelection();
          chunk.markAttributeDirty("a_selection_mask");
        }

      }

    }

  },

  applySelection(chunk, isSelection) {

    const geom = chunk.geometry;

    const selectionMask = geom.getItemBuffer("a_selection_mask");

    if (!selectionMask) {
      return;
    }

    const activeSelectionMask = geom.getItemBuffer("active_selection_mask");

    const selectionValue = isSelection ? 1 : 0;

    let hasSelection = false;

    for (let i = 0; i < activeSelectionMask.length; ++i) {
      if (activeSelectionMask[i] === 1) {
        selectionMask[i] = selectionValue;
        activeSelectionMask[i] = 0;
      }
      if (selectionMask[i] === 1) {
        hasSelection = true;
      }
    }

    geom.setHasMarkedVertices(false);
    geom.setHasSelectedVertices(hasSelection);
    chunk.markAttributeDirty("a_selection_mask");

  },

  findObjectIntersection(destPoint, x, y, camera, object) {
    const origin = camera.calculateOrigin(x, y, _VEC3_TMP);

    const ray = camera.calculateRay(x, y, _VEC3_TMP2);
    const point = _VEC3_TMP3;

    let distance = Infinity;
    let hitted = false;

    if (object.castRay(origin, ray, point)) {

      hitted = true;

      const dist = vec3.distance(point, origin);

      if (dist < distance) {

        distance = dist;
        vec3.copy(destPoint, point);

      }
    }

    return hitted ? distance : -1.0;
  },

  findClosestIntersection: (function () {

    const tmpHitPoint = vec3.create();

    return function (outHitPoint, x, y, camera, objects) {

      let intersectedObject = null;

      let distance = Infinity;

      for (let object of objects) {

        const dist = this.findObjectIntersection(tmpHitPoint, x, y, camera, object);

        if (dist >= 0.0) {

          if (dist < distance) {

            if (outHitPoint) {
              vec3.copy(outHitPoint, tmpHitPoint);
            }

            distance = dist;

            intersectedObject = object;

          }

        }

      }

      return intersectedObject;
    }
  }()),

}
const numberToObjectSpace = (function() {

  const vec4Tmp = vec4.create();

  return function(value, toLocal) {

    vec4.set(vec4Tmp, value, 0, 0, 0);
    vec4.transformMat4(vec4Tmp, vec4Tmp, toLocal);

    return Math.sign(value) * vec4.length(vec4Tmp);

  };

})();
